import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { ResponsiveImage, Modal, IconArrowHead } from '../../components';
import FavouriteButton from '../../components/FavouriteButton/FavouriteButton';

import ImageCarousel from './ImageCarousel/ImageCarousel';
import { SQUARE_VARIANTS, FULLSCREEN_VARIANTS } from './listingImageVariants';

import css from './ListingPage.module.css';
import { ReactComponent as CertifiedIcon } from '../../assets/Certified.svg';
import { ReactComponent as FireIcon } from '../../assets/FireOutline.svg';

const SectionHero = props => {
  const {
    title,
    listing,
    isOwnListing,
    handleViewPhotosClick,
    imageCarouselOpen,
    initialImageIndex = 0,
    onImageCarouselClose,
    onManageDisableScrolling,
    actionBar,
    imageVariants = SQUARE_VARIANTS,
    fullscreenVariants = FULLSCREEN_VARIANTS,
    thumbnailVariants = [],
    isPartner = false,
    isTrustedSeller = false,
    isHotDeal = false,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(initialImageIndex);
  const thumbnailsRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setActiveIndex(initialImageIndex);
  }, [initialImageIndex, listing?.id]);

  const images = useMemo(() => listing?.images || [], [listing]);
  const hasImages = images.length > 0;
  const activeImage = hasImages ? images[activeIndex] : null;

  const handleArrow = direction => {
    if (thumbnailsRef.current) {
      thumbnailsRef.current.scrollBy({ left: direction * 96, behavior: 'smooth' });
    }

    setActiveIndex(prev => {
      const next = prev + direction;
      if (next < 0) {
        return 0;
      }
      if (next > images.length - 1) {
        return images.length - 1;
      }
      return next;
    });
  };

  const handleMainImageClick = () => {
    if (hasImages) {
      handleViewPhotosClick(activeIndex);
    }
  };

  const handleThumbnailClick = (event, index) => {
    setActiveIndex(index);
    event.currentTarget?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const thumbnails = thumbnailVariants.length > 0 ? thumbnailVariants : imageVariants;
  const handleMainImageKeyDown = event => {
    if (!hasImages) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleViewPhotosClick(activeIndex);
    }
  };

  return (
    <section className={css.sectionHero} data-testid="hero">
      <div className={css.heroMedia}>
        <div
          className={css.heroImageWrapper}
          role="button"
          tabIndex={0}
      onClick={handleMainImageClick}
      onKeyDown={handleMainImageKeyDown}
    >
        {isPartner || isTrustedSeller || isHotDeal ? (
          <div className={css.heroBadgeStack}>
            {isHotDeal ? (
              <span className={classNames(css.heroPartnerBadge, css.heroHotBadge)}>
                <FireIcon className={css.heroHotIcon} aria-hidden="true" />
                <span className={css.heroBadgeLabel}>Hot Deal</span>
              </span>
            ) : null}
            {isPartner ? (
              <span className={css.heroPartnerBadge}>
                <CertifiedIcon className={css.heroPartnerIcon} aria-hidden="true" />
                <span className={css.heroBadgeLabel}>Verified Rigger</span>
              </span>
            ) : null}
            {isTrustedSeller ? (
              <span className={css.heroPartnerBadge}>
                <CertifiedIcon className={css.heroPartnerIcon} aria-hidden="true" />
                <span className={css.heroBadgeLabel}>Trusted Seller</span>
              </span>
            ) : null}
          </div>
        ) : null}
        {mounted && listing?.id && isOwnListing ? (
          <div onClick={e => e.stopPropagation()} className={css.actionBarContainerForHeroLayout}>
            {actionBar}
          </div>
        ) : null}

          {hasImages ? (
            <ResponsiveImage
              rootClassName={css.heroImage}
              alt={title}
              image={activeImage}
              variants={imageVariants}
              sizes="(max-width: 640px) 100vw, 640px"
              fetchpriority="high"
            />
          ) : (
            <div className={css.heroPlaceholder} />
          )}

          {hasImages ? (
            <span className={css.heroImageCount}>
              {activeIndex + 1}/{images.length}
            </span>
          ) : null}
          {listing?.id?.uuid ? (
            <FavouriteButton
              listingId={listing.id.uuid}
              variant="card"
              className={css.heroFavouriteButton}
            />
          ) : null}
        </div>

        {hasImages ? (
          <div className={css.thumbnailBar}>
            <button
              type="button"
              className={classNames(css.thumbnailNav, css.thumbnailNavPrev)}
              onClick={() => handleArrow(-1)}
              disabled={activeIndex === 0}
              aria-label="Previous image"
            >
              <IconArrowHead direction="left" size="small" />
            </button>

            <div className={css.thumbnailList} ref={thumbnailsRef}>
              {images.map((img, index) => (
                <button
                  type="button"
                  key={img.id?.uuid || index}
                  className={classNames(css.thumbnailButton, {
                    [css.thumbnailButtonActive]: index === activeIndex,
                  })}
                  onClick={event => handleThumbnailClick(event, index)}
                  aria-label={`Show image ${index + 1}`}
                >
                  <ResponsiveImage
                    rootClassName={css.thumbnailImage}
                    image={img}
                    alt=""
                    variants={thumbnails}
                    sizes="84px"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              className={classNames(css.thumbnailNav, css.thumbnailNavNext)}
              onClick={() => handleArrow(1)}
              disabled={activeIndex === images.length - 1}
              aria-label="Next image"
            >
              <IconArrowHead direction="right" size="small" />
            </button>
          </div>
        ) : null}
      </div>

      <Modal
        id="ListingPage.imageCarousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={imageCarouselOpen}
        onClose={onImageCarouselClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel
          images={images}
          imageVariants={fullscreenVariants}
          startIndex={activeIndex}
        />
      </Modal>
    </section>
  );
};

export default SectionHero;
